import React, { useState, useEffect, useMemo, useRef, useCallback, useTransition, useDeferredValue } from 'react';
import { Spinner, Alert, Badge, Modal, Form, Button } from 'react-bootstrap';
import { MapContainer, TileLayer, Polyline, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import apiClient from '../../api/client';
import { Settings, RefreshCw, ChevronUp, ChevronDown, Search, X, Navigation } from 'lucide-react';

// ── Constants ──────────────────────────────────────────────────────────────────
const SAKBE_KEY = 'Jq92BpFD-tYae-BBj2-rEMc-MnuytuOB30ST';
const MINI_SHEET_H = 136; // px — collapsed bottom sheet height

// ── Helpers ────────────────────────────────────────────────────────────────────
const formatNum = (n) => (n == null ? '—' : Number(n).toLocaleString('es-MX'));

const formatLitros = (n) => {
    if (n == null) return '—';
    return n >= 1000 ? `${(n / 1000).toFixed(1)}k L` : `${n} L`;
};

const haversineKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos((lat1 * Math.PI) / 180) *
            Math.cos((lat2 * Math.PI) / 180) *
            Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const distPointToSegmentKm = (pLat, pLon, aLat, aLon, bLat, bLon) => {
    const dx = bLon - aLon;
    const dy = bLat - aLat;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) return haversineKm(pLat, pLon, aLat, aLon);
    const t = Math.max(0, Math.min(1, ((pLon - aLon) * dx + (pLat - aLat) * dy) / lenSq));
    return haversineKm(pLat, pLon, aLat + t * dy, aLon + t * dx);
};

const convertirCoordenadasGeoJSON = (geojsonInput) => {
    try {
        const geojson =
            typeof geojsonInput === 'string' ? JSON.parse(geojsonInput) : geojsonInput;
        if (!geojson) return null;
        if (geojson.type === 'MultiLineString')
            return geojson.coordinates.map((line) => line.map(([lng, lat]) => [lat, lng]));
        if (geojson.type === 'LineString')
            return [geojson.coordinates.map(([lng, lat]) => [lat, lng])];
    } catch { /* ignore */ }
    return null;
};

// ── Leaflet map helpers ────────────────────────────────────────────────────────
function MapFlyTo({ route }) {
    const map = useMap();
    const prevIdRef = useRef(null);
    useEffect(() => {
        if (!route || prevIdRef.current === route.id_Tipo_ruta) return;
        prevIdRef.current = route.id_Tipo_ruta;
        const midLat = (route.LatOrigen + route.LatDestino) / 2;
        const midLng = (route.LonOrigen + route.LonDestino) / 2;
        if (midLat && midLng) map.flyTo([midLat, midLng], 7, { duration: 1.0 });
    }, [route, map]);
    return null;
}

function MapResizer({ trigger }) {
    const map = useMap();
    useEffect(() => {
        const t = setTimeout(() => map.invalidateSize(), 150);
        return () => clearTimeout(t);
    }, [trigger, map]);
    return null;
}

// ── INEGI population selector modal ───────────────────────────────────────────
function RouteInegiModal({ route, onConfirm, onClose }) {
    const [origenQuery,    setOrigenQuery]    = useState(route?.Origen  || '');
    const [destinoQuery,   setDestinoQuery]   = useState(route?.Destino || '');
    const [origenResults,  setOrigenResults]  = useState([]);
    const [destinoResults, setDestinoResults] = useState([]);
    const [origenSel,      setOrigenSel]      = useState(null);
    const [destinoSel,     setDestinoSel]     = useState(null);
    const [loadingO,       setLoadingO]       = useState(false);
    const [loadingD,       setLoadingD]       = useState(false);
    const [submitting,     setSubmitting]     = useState(false);

    useEffect(() => {
        if (!route) return;
        setOrigenQuery(route.Origen  || '');
        setDestinoQuery(route.Destino || '');
        setOrigenSel(null);
        setDestinoSel(null);
        setOrigenResults([]);
        setDestinoResults([]);
    }, [route?.id_Tipo_ruta]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounced INEGI search — origin
    useEffect(() => {
        if (!origenQuery || origenQuery.length < 2) { setOrigenResults([]); return; }
        const t = setTimeout(async () => {
            setLoadingO(true);
            try {
                const fd = new URLSearchParams({ buscar: origenQuery, type: 'json', num: 8, key: SAKBE_KEY });
                const res = await fetch('https://gaia.inegi.org.mx/sakbe_v3.1/buscadestino', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: fd,
                });
                const d = await res.json();
                setOrigenResults(d.data || []);
            } catch { setOrigenResults([]); }
            finally  { setLoadingO(false); }
        }, 380);
        return () => clearTimeout(t);
    }, [origenQuery]);

    // Debounced INEGI search — destination
    useEffect(() => {
        if (!destinoQuery || destinoQuery.length < 2) { setDestinoResults([]); return; }
        const t = setTimeout(async () => {
            setLoadingD(true);
            try {
                const fd = new URLSearchParams({ buscar: destinoQuery, type: 'json', num: 8, key: SAKBE_KEY });
                const res = await fetch('https://gaia.inegi.org.mx/sakbe_v3.1/buscadestino', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: fd,
                });
                const d = await res.json();
                setDestinoResults(d.data || []);
            } catch { setDestinoResults([]); }
            finally  { setLoadingD(false); }
        }, 380);
        return () => clearTimeout(t);
    }, [destinoQuery]);

    const handleConfirm = async () => {
        if (!origenSel || !destinoSel || submitting) return;
        setSubmitting(true);
        try {
            await onConfirm(origenSel.id_dest, destinoSel.id_dest);
        } catch {
            setSubmitting(false);
        }
    };

    const ResultList = ({ results, loading, selected, onSelect }) => (
        <div style={{
            border: '1px solid #dee2e6', borderRadius: 6,
            maxHeight: 150, overflowY: 'auto',
            background: '#fff', fontSize: '12px',
        }}>
            {loading && (
                <div className="text-center p-2">
                    <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                </div>
            )}
            {!loading && results.length === 0 && (
                <div className="text-muted text-center py-3" style={{ fontSize: '12px' }}>Sin resultados</div>
            )}
            {results.map((item) => (
                <div
                    key={item.id_dest}
                    onClick={() => onSelect(item)}
                    style={{
                        padding: '7px 10px', cursor: 'pointer',
                        background: selected?.id_dest === item.id_dest ? '#cce5ff' : 'transparent',
                        borderBottom: '1px solid #f3f3f3',
                    }}
                >
                    <strong>{item.nombre}</strong>
                    {(item.nombre_mun || item.nombre_ent) && (
                        <span className="text-muted ml-1">
                            {[item.nombre_mun, item.nombre_ent].filter(Boolean).join(', ')}
                        </span>
                    )}
                </div>
            ))}
        </div>
    );

    return (
        <Modal show={!!route} onHide={onClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: '14px' }}>
                    <i className="fas fa-map-marked-alt mr-2 text-primary" />
                    Seleccionar poblaciones INEGI
                    {route && (
                        <span className="text-muted ml-2" style={{ fontSize: '12px', fontWeight: 'normal' }}>
                            — {route.Origen} → {route.Destino}
                        </span>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-2">
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <Form.Label className="mb-1 font-weight-bold" style={{ fontSize: '12px', color: '#0069d9' }}>
                            Origen INEGI{' '}
                            <span className="text-muted font-weight-normal">({route?.Origen})</span>
                        </Form.Label>
                        <Form.Control
                            size="sm" type="text" placeholder="Buscar ciudad..."
                            value={origenQuery}
                            onChange={(e) => { setOrigenQuery(e.target.value); setOrigenSel(null); }}
                            className="mb-1"
                        />
                        {origenSel && (
                            <div className="mb-1 px-2 py-1 rounded" style={{ background: '#cce5ff', fontSize: '11px' }}>
                                ✓ <strong>{origenSel.nombre}</strong>
                            </div>
                        )}
                        <ResultList results={origenResults} loading={loadingO} selected={origenSel} onSelect={setOrigenSel} />
                    </div>
                    <div className="col-md-6 mb-3">
                        <Form.Label className="mb-1 font-weight-bold" style={{ fontSize: '12px', color: '#28a745' }}>
                            Destino INEGI{' '}
                            <span className="text-muted font-weight-normal">({route?.Destino})</span>
                        </Form.Label>
                        <Form.Control
                            size="sm" type="text" placeholder="Buscar ciudad..."
                            value={destinoQuery}
                            onChange={(e) => { setDestinoQuery(e.target.value); setDestinoSel(null); }}
                            className="mb-1"
                        />
                        {destinoSel && (
                            <div className="mb-1 px-2 py-1 rounded" style={{ background: '#d4edda', fontSize: '11px' }}>
                                ✓ <strong>{destinoSel.nombre}</strong>
                            </div>
                        )}
                        <ResultList results={destinoResults} loading={loadingD} selected={destinoSel} onSelect={setDestinoSel} />
                    </div>
                </div>
                <small className="text-muted" style={{ fontSize: '11px' }}>
                    <i className="fas fa-info-circle mr-1" />
                    Selecciona la localidad correcta para cada extremo de la ruta.
                </small>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" size="sm" onClick={onClose}>Cancelar</Button>
                <Button
                    variant="primary" size="sm"
                    disabled={!origenSel || !destinoSel || submitting}
                    onClick={handleConfirm}
                >
                    <i className={`fas ${submitting ? 'fa-spinner fa-spin' : 'fa-route'} mr-1`} />
                    {submitting ? 'Trazando...' : 'Trazar ruta'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

// Validates that a coordinate pair falls within Mexico's bounding box
const inMexico = (lat, lon) =>
    lat >= 14.5 && lat <= 32.7 && lon >= -118.5 && lon <= -86.7;

// Memoized so only the 2 cards whose `isSel` flipped re-render on route change
const RouteCard = React.memo(({ r, isSel, onSelect }) => (
    <div
        onClick={() => onSelect(r)}
        style={{
            padding: '12px 14px', marginBottom: 6, borderRadius: 12,
            background: isSel ? '#e8f4fd' : '#f8f9fa',
            border: `1.5px solid ${isSel ? '#007bff' : '#dee2e6'}`,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 10,
            WebkitTapHighlightColor: 'transparent',
        }}
    >
        <Navigation
            size={18}
            color={isSel ? '#007bff' : '#adb5bd'}
            fill={isSel ? '#007bff' : 'none'}
            style={{ flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#343a40' }}>
                <span style={{ color: '#007bff' }}>{r.Origen}</span>
                {' → '}
                <span style={{ color: '#28a745' }}>{r.Destino}</span>
                {r.Alterna && (
                    <span
                        style={{
                            marginLeft: 5,
                            background: '#fff3cd', border: '1px solid #ffc107',
                            borderRadius: 4, padding: '0 4px',
                            fontSize: '9px', fontWeight: 600,
                        }}
                    >
                        Alt
                    </span>
                )}
            </div>
            <div style={{ fontSize: '11px', color: '#6c757d', marginTop: 3 }}>
                {r.Km_reales || '?'} km &nbsp;·&nbsp;
                {formatNum(r.ViajesTotal)} viajes tot. &nbsp;·&nbsp;
                Est. {formatLitros(r.estAcum)}
            </div>
            <div style={{ fontSize: '10px', color: '#adb5bd', marginTop: 2 }}>
                {[2021, 2022, 2023, 2024, 2025, 2026]
                    .filter(y => (r[`Viajes${y}`] || 0) > 0)
                    .map(y => `${y}: ${r[`Viajes${y}`]}`)
                    .join(' · ')}
            </div>
        </div>
    </div>
));

// ── Main Mobile Component ──────────────────────────────────────────────────────
const CombustibleMapaMovil = () => {
    const [loading,          setLoading]          = useState(true);
    const [error,            setError]            = useState(null);
    const [routes,           setRoutes]           = useState([]);

    // Store only the ID; derive the full processed object so it stays fresh on rendimiento change
    const [selectedRouteId,  setSelectedRouteId]  = useState(null);
    const [rendimiento,      setRendimiento]      = useState(2.5);

    // Bottom sheet: collapsed by default, expanded for search
    const [sheetOpen,        setSheetOpen]        = useState(false);
    const [filterText,       setFilterText]       = useState('');

    // INEGI Sakbe road polyline for the selected route
    const [polylineData,     setPolylineData]     = useState(null); // { status, coords? }
    const currentRouteIdRef                       = useRef(null);
    const [inegiModalRoute,  setInegiModalRoute]  = useState(null);

    // OXXO Gas stations
    const [stations,         setStations]         = useState([]);
    const [loadingStations,  setLoadingStations]  = useState(false);
    const [showStations,     setShowStations]     = useState(false);
    const [corridorKm,       setCorridorKm]       = useState(50);

    // Settings panel
    const [showSettings,     setShowSettings]     = useState(false);

    const [, startTransition] = useTransition();
    const deferredFilter      = useDeferredValue(filterText);

    // Block body scroll while this full-screen component is mounted
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    // ── Fetch routes ──────────────────────────────────────────────────────
    const fetchRoutes = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await apiClient.get('/api/casetas/analisis/combustible-historico');
            setRoutes(data);
        } catch (err) {
            setError('Error al cargar rutas: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchRoutes(); }, [fetchRoutes]);

    // ── Fetch OXXO stations (lazy — only when layer is toggled on) ────────
    useEffect(() => {
        if (!showStations || stations.length > 0) return;
        const load = async () => {
            setLoadingStations(true);
            try {
                const { data } = await apiClient.get('/api/casetas/analisis/oxxo-stations');
                setStations(data);
            } catch (err) {
                console.error('Error cargando estaciones OXXO:', err);
            } finally {
                setLoadingStations(false);
            }
        };
        load();
    }, [showStations, stations.length]);

    // ── Fetch INEGI Sakbe polyline (auto, by route name) ──────────────────
    const fetchPolylineForRoute = useCallback(async (route) => {
        const id = route.id_Tipo_ruta;
        setPolylineData({ status: 'loading' });
        try {
            const sakbeSearch = async (query) => {
                const fd = new URLSearchParams({ buscar: query, type: 'json', num: 5, key: SAKBE_KEY });
                const res = await fetch('https://gaia.inegi.org.mx/sakbe_v3.1/buscadestino', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: fd,
                });
                const d = await res.json();
                return d.data || [];
            };

            const [origenes, destinos] = await Promise.all([
                sakbeSearch(route.Origen),
                sakbeSearch(route.Destino),
            ]);

            const origenId  = origenes?.[0]?.id_dest;
            const destinoId = destinos?.[0]?.id_dest;
            if (!origenId || !destinoId)
                throw new Error(`IDs no encontrados (${route.Origen}→${route.Destino})`);

            const fd = new URLSearchParams({
                dest_i: origenId, dest_f: destinoId, v: '5', e: '0', type: 'json', key: SAKBE_KEY,
            });
            const res = await fetch('https://gaia.inegi.org.mx/sakbe_v3.1/optima', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: fd,
            });
            const data    = await res.json();
            const geojson = data?.data?.geojson;
            if (!geojson) throw new Error('Sin geojson en respuesta INEGI');
            const coords = convertirCoordenadasGeoJSON(geojson);
            if (!coords)  throw new Error('GeoJSON no reconocido');

            if (currentRouteIdRef.current !== id) return; // Discard stale response
            setPolylineData({ status: 'ready', coords });
        } catch (err) {
            console.warn(`Ruta real no disponible (${route.Origen}→${route.Destino}):`, err.message);
            if (currentRouteIdRef.current !== id) return;
            setPolylineData({ status: 'error' });
        }
    }, []);

    // Fetch polyline using manually selected INEGI locality IDs (from modal)
    const fetchPolylineWithIds = useCallback(async (route, origenId, destinoId) => {
        const id = route.id_Tipo_ruta;
        currentRouteIdRef.current = id;
        setPolylineData({ status: 'loading' });
        try {
            const fd = new URLSearchParams({
                dest_i: origenId, dest_f: destinoId, v: '5', e: '0', type: 'json', key: SAKBE_KEY,
            });
            const res = await fetch('https://gaia.inegi.org.mx/sakbe_v3.1/optima', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: fd,
            });
            const data    = await res.json();
            const geojson = data?.data?.geojson;
            if (!geojson) throw new Error('Sin geojson en respuesta INEGI');
            const coords = convertirCoordenadasGeoJSON(geojson);
            if (!coords)  throw new Error('GeoJSON no reconocido');
            if (currentRouteIdRef.current !== id) return;
            setPolylineData({ status: 'ready', coords });
        } catch (err) {
            console.warn('Error al trazar ruta con IDs manuales:', err.message);
            if (currentRouteIdRef.current !== id) return;
            setPolylineData({ status: 'error' });
        }
    }, []);

    // Trigger polyline fetch whenever the selected route changes
    useEffect(() => {
        if (!selectedRouteId) { setPolylineData(null); return; }
        currentRouteIdRef.current = selectedRouteId;
        setPolylineData(null);
        const route = processedRoutes.find((r) => r.id_Tipo_ruta === selectedRouteId);
        if (route) fetchPolylineForRoute(route);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRouteId]);

    // ── Processed routes (recalculated on rendimiento change) ─────────────
    const processedRoutes = useMemo(
        () =>
            routes.map((r) => {
                const km    = parseFloat(r.Km_reales) || 0;
                const trips = r.ViajesTotal           || 0;
                const estProm = km > 0 && rendimiento > 0 ? Math.round(km / rendimiento) : 0;
                const estAcum = Math.round(trips * estProm);
                return {
                    ...r, estAcum, estProm,
                    LatOrigen:  parseFloat(r.LatOrigen),
                    LonOrigen:  parseFloat(r.LonOrigen),
                    LatDestino: parseFloat(r.LatDestino),
                    LonDestino: parseFloat(r.LonDestino),
                };
            }),
        [routes, rendimiento]
    );

    const validRoutes = useMemo(
        () =>
            processedRoutes.filter(
                (r) =>
                    r.LatOrigen  && r.LonOrigen  &&
                    r.LatDestino && r.LonDestino &&
                    inMexico(r.LatOrigen, r.LonOrigen) &&
                    inMexico(r.LatDestino, r.LonDestino)
            ),
        [processedRoutes]
    );

    // Always derive selectedRoute from processedRoutes so estAcum stays fresh
    const selectedRoute = useMemo(
        () => processedRoutes.find((r) => r.id_Tipo_ruta === selectedRouteId) || null,
        [processedRoutes, selectedRouteId]
    );

    const sortedRoutes = useMemo(
        () => [...validRoutes].sort((a, b) => (b.ViajesTotal || 0) - (a.ViajesTotal || 0)),
        [validRoutes]
    );

    const filteredRoutes = useMemo(() => {
        if (!deferredFilter) return sortedRoutes;
        const q = deferredFilter.toLowerCase();
        return sortedRoutes.filter(
            (r) =>
                (r.Origen        || '').toLowerCase().includes(q) ||
                (r.Destino       || '').toLowerCase().includes(q) ||
                (r.NombreOrigen  || '').toLowerCase().includes(q) ||
                (r.NombreDestino || '').toLowerCase().includes(q)
        );
    }, [sortedRoutes, deferredFilter]);

    // OXXO stations within the corridor of the selected route
    const nearbyStations = useMemo(() => {
        if (!showStations || stations.length === 0 || !selectedRoute) return [];
        const r = selectedRoute;
        return stations.filter((st) => {
            if (polylineData?.status === 'ready' && polylineData.coords?.length > 0) {
                return polylineData.coords.some((line) =>
                    line.some((pt, i) => {
                        if (i === 0) return false;
                        return (
                            distPointToSegmentKm(
                                st.lat, st.lon,
                                line[i - 1][0], line[i - 1][1],
                                pt[0], pt[1]
                            ) <= corridorKm
                        );
                    })
                );
            }
            return (
                distPointToSegmentKm(st.lat, st.lon, r.LatOrigen, r.LonOrigen, r.LatDestino, r.LonDestino) <=
                corridorKm
            );
        });
    }, [showStations, stations, selectedRoute, corridorKm, polylineData]);

    // ── Derived map rendering data ─────────────────────────────────────────
    const routeSegs = useMemo(() => {
        if (!selectedRoute) return [];
        if (polylineData?.status === 'ready') return polylineData.coords;
        // Straight-line fallback while loading or on error
        return [
            [
                [selectedRoute.LatOrigen,  selectedRoute.LonOrigen],
                [selectedRoute.LatDestino, selectedRoute.LonDestino],
            ],
        ];
    }, [selectedRoute, polylineData]);

    const routeColor =
        polylineData?.status === 'ready'   ? '#6f42c1' :
        polylineData?.status === 'error'   ? '#fd7e14' :
        '#17a2b8';

    const routeDash =
        polylineData?.status === 'loading' ? '8 6'     :
        polylineData?.status === 'ready'   ? undefined :
        '5 4';

    const handleSelectRoute = useCallback((r) => {
        setSelectedRouteId(r.id_Tipo_ruta);
        setSheetOpen(false);
        setFilterText('');
    }, []);

    // ── Loading screen ─────────────────────────────────────────────────────
    if (loading)
        return (
            <div
                style={{
                    position: 'fixed', inset: 0, zIndex: 2000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: '#f8f9fa', flexDirection: 'column',
                }}
            >
                <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
                <p className="mt-3 text-muted" style={{ fontSize: '14px' }}>Cargando rutas...</p>
            </div>
        );

    const sheetH = sheetOpen ? '76vh' : MINI_SHEET_H;

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: '#e9ecef', overflow: 'hidden' }}>

            {/* ── Leaflet map fills the entire viewport ── */}
            <MapContainer
                center={[23.6, -102.5]}
                zoom={5}
                style={{ position: 'absolute', inset: 0, zIndex: 0 }}
                zoomControl={false}
            >
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                {selectedRoute && <MapFlyTo route={selectedRoute} />}
                <MapResizer trigger={sheetOpen} />

                {/* Route polyline(s) */}
                {routeSegs.map((seg, si) => (
                    <Polyline
                        key={`seg-${si}`}
                        positions={seg}
                        pathOptions={{ color: routeColor, weight: 6, opacity: 0.9, dashArray: routeDash }}
                    >
                        {si === 0 && selectedRoute && (
                            <Popup>
                                <div style={{ fontSize: '13px', minWidth: '180px' }}>
                                    <strong>
                                        {selectedRoute.Origen} → {selectedRoute.Destino}
                                    </strong>
                                    <hr style={{ margin: '4px 0' }} />
                                    <div>{selectedRoute.Km_reales || '?'} km</div>
                                    <div style={{ color: '#17a2b8' }}>
                                        Est. {formatLitros(selectedRoute.estAcum)} acum. histórico
                                    </div>
                                    <div style={{ color: '#17a2b8' }}>
                                        {formatNum(selectedRoute.estProm)} L/viaje
                                    </div>
                                    <hr style={{ margin: '4px 0' }} />
                                    <div style={{ fontSize: '11px', color: '#6c757d' }}>
                                        {[2021,2022,2023,2024,2025,2026]
                                            .filter(y => (selectedRoute[`Viajes${y}`] || 0) > 0)
                                            .map(y => `${y}: ${selectedRoute[`Viajes${y}`]}`)
                                            .join(' · ')}
                                    </div>
                                </div>
                            </Popup>
                        )}
                    </Polyline>
                ))}

                {/* Origin & destination circle markers */}
                {selectedRoute && (
                    <>
                        <CircleMarker
                            center={[selectedRoute.LatOrigen, selectedRoute.LonOrigen]}
                            radius={9}
                            pathOptions={{ color: routeColor, fillColor: routeColor, fillOpacity: 0.9, weight: 2 }}
                        >
                            <Popup>
                                <strong>{selectedRoute.Origen}</strong>
                                {selectedRoute.NombreOrigen && (
                                    <div style={{ fontSize: '11px' }}>{selectedRoute.NombreOrigen}</div>
                                )}
                            </Popup>
                        </CircleMarker>
                        <CircleMarker
                            center={[selectedRoute.LatDestino, selectedRoute.LonDestino]}
                            radius={9}
                            pathOptions={{ color: routeColor, fillColor: '#fff', fillOpacity: 1, weight: 3 }}
                        >
                            <Popup>
                                <strong>{selectedRoute.Destino}</strong>
                                {selectedRoute.NombreDestino && (
                                    <div style={{ fontSize: '11px' }}>{selectedRoute.NombreDestino}</div>
                                )}
                            </Popup>
                        </CircleMarker>
                    </>
                )}

                {/* OXXO Gas stations within corridor */}
                {nearbyStations.map((st, idx) => (
                    <CircleMarker
                        key={`st-${idx}`}
                        center={[st.lat, st.lon]}
                        radius={7}
                        pathOptions={{ color: '#e65100', fillColor: '#ff6f00', fillOpacity: 0.9, weight: 1.5 }}
                    >
                        <Popup>
                            <div style={{ fontSize: '13px', minWidth: '130px' }}>
                                <strong style={{ color: '#e65100' }}>⛽ OXXO Gas</strong>
                                <div style={{ marginTop: 4 }}>{st.name}</div>
                            </div>
                        </Popup>
                    </CircleMarker>
                ))}
            </MapContainer>

            {/* ── Floating top bar ── */}
            <div
                style={{
                    position: 'absolute', top: 14, left: 12, right: 12, zIndex: 900,
                    background: 'rgba(255,255,255,0.96)',
                    borderRadius: 14, padding: '9px 12px',
                    boxShadow: '0 2px 14px rgba(0,0,0,0.18)',
                    display: 'flex', alignItems: 'center', gap: 8,
                }}
            >
                <i className="fas fa-gas-pump text-primary" style={{ fontSize: 17, flexShrink: 0 }} />

                <div style={{ flex: 1, minWidth: 0 }}>
                    {selectedRoute ? (
                        <>
                            <div
                                style={{
                                    fontSize: '13px', fontWeight: 700, color: '#343a40',
                                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }}
                            >
                                {selectedRoute.Origen} → {selectedRoute.Destino}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6c757d' }}>
                                {selectedRoute.Km_reales || '?'} km &nbsp;·&nbsp;
                                {rendimiento} km/L
                                {polylineData?.status === 'loading' && (
                                    <>
                                        &nbsp;·&nbsp;
                                        <Spinner
                                            animation="border" size="sm"
                                            style={{ width: 10, height: 10, borderWidth: 1 }}
                                        />{' '}
                                        INEGI
                                    </>
                                )}
                                {polylineData?.status === 'ready' && (
                                    <> &nbsp;·&nbsp; <span style={{ color: '#6f42c1' }}>🗺️ Real</span></>
                                )}
                                {polylineData?.status === 'error' && (
                                    <> &nbsp;·&nbsp; <span style={{ color: '#fd7e14' }}>⚠️ Aprox.</span></>
                                )}
                            </div>
                        </>
                    ) : (
                        <div style={{ fontSize: '13px', color: '#6c757d' }}>
                            Gasolineras EOX — Selecciona tu ruta
                        </div>
                    )}
                </div>

                {/* OXXO Gas layer toggle */}
                <button
                    onClick={() => setShowStations((s) => !s)}
                    title="Mostrar/ocultar estaciones OXXO Gas"
                    style={{
                        background: showStations ? '#ff6f00' : 'transparent',
                        border: `2px solid ${showStations ? '#ff6f00' : '#ced4da'}`,
                        borderRadius: 10, padding: '6px 8px',
                        fontSize: '16px', cursor: 'pointer',
                        minWidth: 42, minHeight: 42,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    {loadingStations ? (
                        <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
                    ) : (
                        '⛽'
                    )}
                </button>

                {/* Settings toggle */}
                <button
                    onClick={() => setShowSettings((s) => !s)}
                    title="Configuración"
                    style={{
                        background: showSettings ? '#e9ecef' : 'transparent',
                        border: '2px solid #ced4da',
                        borderRadius: 10, padding: '7px',
                        cursor: 'pointer',
                        minWidth: 42, minHeight: 42,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Settings size={16} color="#495057" />
                </button>
            </div>

            {/* ── Settings dropdown panel ── */}
            {showSettings && (
                <div
                    style={{
                        position: 'absolute', top: 76, left: 12, right: 12, zIndex: 900,
                        background: 'rgba(255,255,255,0.97)',
                        borderRadius: 14, padding: '12px 16px',
                        boxShadow: '0 4px 18px rgba(0,0,0,0.15)',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, flex: 1 }}>
                            <i className="fas fa-cog mr-1 text-primary" /> Configuración
                        </span>
                        <button
                            onClick={() => setShowSettings(false)}
                            style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                fontSize: 20, lineHeight: 1, color: '#6c757d', padding: 0,
                            }}
                        >
                            ×
                        </button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: showStations ? 12 : 0 }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, margin: 0, whiteSpace: 'nowrap' }}>
                            Rendimiento:
                        </label>
                        <input
                            type="range" min="1" max="6" step="0.1"
                            value={rendimiento}
                            onChange={(e) => setRendimiento(Number(e.target.value))}
                            style={{ flex: 1 }}
                        />
                        <Badge variant="primary" style={{ fontSize: '12px', minWidth: 62, textAlign: 'center' }}>
                            {rendimiento} km/L
                        </Badge>
                    </div>

                    {showStations && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <label style={{ fontSize: '12px', fontWeight: 600, margin: 0, whiteSpace: 'nowrap' }}>
                                Corredor OXXO:
                            </label>
                            <input
                                type="range" min="10" max="200" step="5"
                                value={corridorKm}
                                onChange={(e) => setCorridorKm(Number(e.target.value))}
                                style={{ flex: 1 }}
                            />
                            <Badge variant="warning" style={{ fontSize: '12px', minWidth: 54, textAlign: 'center' }}>
                                {corridorKm} km
                            </Badge>
                        </div>
                    )}

                    <small className="text-muted d-block mt-2" style={{ fontSize: '10px' }}>
                        Referencia: ~2–3 km/L camión 5 ejes cargado
                    </small>
                </div>
            )}

            {/* ── INEGI action buttons (float above bottom sheet, only when closed) ── */}
            {selectedRoute && polylineData && !sheetOpen && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: MINI_SHEET_H + 12,
                        right: 12,
                        zIndex: 900,
                        display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end',
                    }}
                >
                    {polylineData.status === 'error' && (
                        <button
                            onClick={() => setInegiModalRoute(selectedRoute)}
                            style={{
                                background: 'rgba(255,243,205,0.97)', border: '1px solid #ffc107',
                                borderRadius: 10, padding: '8px 12px', fontSize: '12px', fontWeight: 600,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)', cursor: 'pointer',
                            }}
                        >
                            ⚠️ Trazar con INEGI
                        </button>
                    )}
                    {polylineData.status === 'ready' && (
                        <button
                            onClick={() => setInegiModalRoute(selectedRoute)}
                            style={{
                                background: 'rgba(212,237,218,0.97)', border: '1px solid #28a745',
                                borderRadius: 10, padding: '8px 12px', fontSize: '12px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)', cursor: 'pointer',
                            }}
                        >
                            🗺️ Corregir INEGI
                        </button>
                    )}
                </div>
            )}

            {/* ── Error alert ── */}
            {error && (
                <div style={{ position: 'absolute', top: 76, left: 12, right: 12, zIndex: 1000 }}>
                    <Alert
                        variant="danger" dismissible
                        onClose={() => setError(null)}
                        style={{ fontSize: '12px', padding: '8px 12px' }}
                    >
                        {error}
                    </Alert>
                </div>
            )}

            {/* ── Bottom sheet ── */}
            <div
                style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: sheetH,
                    background: '#fff',
                    borderRadius: '18px 18px 0 0',
                    boxShadow: '0 -4px 24px rgba(0,0,0,0.14)',
                    zIndex: 800,
                    display: 'flex', flexDirection: 'column',
                    transition: 'height 0.28s cubic-bezier(0.4,0,0.2,1)',
                    overflow: 'hidden',
                }}
            >
                {/* Drag handle + header */}
                <div
                    onClick={() => startTransition(() => setSheetOpen((o) => !o))}
                    style={{ padding: '10px 16px 6px', cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}
                >
                    <div
                        style={{
                            width: 40, height: 4, background: '#dee2e6',
                            borderRadius: 2, margin: '0 auto 10px',
                        }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Search size={15} color="#6c757d" />
                        <span
                            style={{
                                fontSize: '13px', flex: 1,
                                color: sheetOpen ? '#343a40' : '#6c757d',
                                fontWeight: sheetOpen ? 600 : 400,
                            }}
                        >
                            {!sheetOpen && selectedRoute
                                ? `${selectedRoute.Origen} → ${selectedRoute.Destino}`
                                : 'Buscar ruta...'}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {showStations && selectedRoute && nearbyStations.length > 0 && (
                                <span
                                    style={{
                                        background: '#fff3cd', border: '1px solid #ffc107',
                                        borderRadius: 6, padding: '1px 7px',
                                        fontSize: '11px', fontWeight: 700, color: '#856404',
                                    }}
                                >
                                    ⛽ {nearbyStations.length}
                                </span>
                            )}
                            {sheetOpen
                                ? <ChevronDown size={17} color="#6c757d" />
                                : <ChevronUp   size={17} color="#6c757d" />}
                        </div>
                    </div>
                </div>

                {/* Mini state — route stats chips */}
                {!sheetOpen && selectedRoute && (
                    <div
                        style={{
                            padding: '0 12px 10px',
                            display: 'flex', gap: 8, overflowX: 'auto',
                            flexShrink: 0,
                            // Hide scrollbar visually while keeping functionality
                            msOverflowStyle: 'none', scrollbarWidth: 'none',
                        }}
                    >
                        {[
                            { label: 'KM',        value: selectedRoute.Km_reales || '?', color: '#28a745' },
                            { label: 'Viajes',    value: formatNum(selectedRoute.ViajesTotal),   color: '#007bff' },
                            { label: 'L/viaje',   value: formatNum(selectedRoute.estProm), color: '#17a2b8' },
                            { label: 'Est. Acum.', value: formatLitros(selectedRoute.estAcum), color: '#6f42c1' },
                            ...(showStations
                                ? [{ label: '⛽ EOX', value: nearbyStations.length, color: '#e65100' }]
                                : []),
                        ].map(({ label, value, color }) => (
                            <div
                                key={label}
                                style={{
                                    textAlign: 'center', flexShrink: 0,
                                    background: '#f8f9fa', borderRadius: 10,
                                    padding: '5px 12px', minWidth: 72,
                                }}
                            >
                                <div style={{ fontSize: '10px', color: '#6c757d', fontWeight: 600 }}>{label}</div>
                                <div style={{ fontSize: '15px', fontWeight: 700, color }}>{value}</div>
                            </div>
                        ))}

                        {polylineData?.status === 'loading' && (
                            <div
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 5,
                                    flexShrink: 0, color: '#17a2b8', fontSize: '11px', paddingLeft: 4,
                                }}
                            >
                                <Spinner animation="border" size="sm" style={{ width: 13, height: 13 }} />
                                INEGI...
                            </div>
                        )}
                    </div>
                )}

                {!sheetOpen && !selectedRoute && (
                    <div style={{ padding: '4px 16px 12px', fontSize: '12px', color: '#6c757d', textAlign: 'center' }}>
                        Toca para seleccionar una ruta y ver las gasolineras afiliadas
                    </div>
                )}

                {/* Expanded state — search input + route list */}
                {sheetOpen && (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div style={{ padding: '4px 14px 10px', flexShrink: 0 }}>
                            <div style={{ position: 'relative' }}>
                                <input
                                    autoFocus
                                    type="text"
                                    value={filterText}
                                    onChange={(e) => setFilterText(e.target.value)}
                                    placeholder="Origen, destino, terminal..."
                                    style={{
                                        width: '100%', border: '1.5px solid #ced4da',
                                        borderRadius: 10, padding: '10px 38px 10px 12px',
                                        fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                                    }}
                                />
                                {filterText && (
                                    <button
                                        onClick={() => setFilterText('')}
                                        style={{
                                            position: 'absolute', right: 10, top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                                        }}
                                    >
                                        <X size={17} color="#6c757d" />
                                    </button>
                                )}
                            </div>
                            <div
                                style={{
                                    fontSize: '11px', color: '#6c757d', marginTop: 6,
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                }}
                            >
                                <span>{filteredRoutes.length} rutas con coordenadas</span>
                                <button
                                    onClick={fetchRoutes}
                                    style={{
                                        background: 'none', border: 'none', color: '#007bff',
                                        cursor: 'pointer', padding: 0, fontSize: '11px',
                                        display: 'flex', alignItems: 'center', gap: 4,
                                    }}
                                >
                                    <RefreshCw size={11} /> Actualizar
                                </button>
                            </div>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 14px' }}>
                            {filteredRoutes.map((r) => (
                                <RouteCard
                                    key={r.id_Tipo_ruta}
                                    r={r}
                                    isSel={selectedRoute?.id_Tipo_ruta === r.id_Tipo_ruta}
                                    onSelect={handleSelectRoute}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* ── INEGI population selector modal ── */}
            <RouteInegiModal
                route={inegiModalRoute}
                onConfirm={async (origenId, destinoId) => {
                    await fetchPolylineWithIds(inegiModalRoute, origenId, destinoId);
                    setInegiModalRoute(null);
                }}
                onClose={() => setInegiModalRoute(null)}
            />
        </div>
    );
};

export default CombustibleMapaMovil;
